import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Users, MapPin, Activity, AlertTriangle, ShieldCheck } from 'lucide-react';
import { KPICard } from '../components/KPICard';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/Button';
import { useNavigate } from 'react-router-dom';
import { BINDA_LIST } from '../data/binda';
import { useStore } from '../data/store';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { FilterBar, FilterField } from '../components/FilterBar';
import { Filter } from 'lucide-react';
import { SyncButton } from '../components/SyncButton';
export default function KoordinatorWilayahDashboard() {
  const navigate = useNavigate();
  const personnelList = useStore(s => s.personnel);
  const alertsList = useStore(s => s.alerts);
  // Simulate getting the region for the current Koordinator Wilayah.
  const regionData = BINDA_LIST.find(b => b.provinsi === 'Jawa Barat')!;
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleResetFilters = () => {
    setFilterValues({});
  };
  const filterFields: FilterField[] = [{
    key: 'date',
    label: 'Periode Waktu',
    type: 'datePreset'
  }, {
    key: 'status',
    label: 'Status Personel',
    type: 'statusBadge',
    options: [{
      label: 'Aktif',
      value: 'Aktif'
    }, {
      label: 'Luring',
      value: 'Luring'
    }]
  }];
  const regionalPersonnel = personnelList.filter(p => p.region === regionData.provinsi);
  const regionalAlerts = alertsList.filter(a => a.location === regionData.provinsi);
  const laporanMasuk = useStore(s => s.reports).filter(r => r.region === regionData.provinsi && r.stage === 'Dikirim');
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-1369">
      <div className="flex justify-between items-end border-b border-border/50 pb-4" data-id="element-1370">
        <div data-id="element-1371">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground uppercase" data-id="element-1372">
            KOORDINASI OPERASIONAL: {regionData.provinsi}
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-1373">
            Manajemen Personel Lapangan & Insiden
          </p>
        </div>
        <div className="flex gap-3 items-center" data-id="element-1374">
          <Button variant={showFilters ? 'default' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 border-border/50 font-mono text-xs ${showFilters ? 'bg-primary text-primary-foreground' : 'bg-background/50'}`} data-id="element-1375">
            <Filter className="w-4 h-4 mr-2" data-id="element-1376" />
            Filter
          </Button>
          <SyncButton data-id="element-1377" />
          <StatusBadge status={regionData.status} className="text-xs px-3 py-1.5" showDot data-id="element-1378" />
        </div>
      </div>

      {showFilters && <FilterBar fields={filterFields} values={filterValues} onChange={handleFilterChange} onApply={() => {}} onReset={handleResetFilters} data-id="element-1379" />}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-id="element-1380">
        <KPICard title="Personel Lapangan" value={regionalPersonnel.length} icon={Users} colorVar="var(--primary)" onClick={() => navigate('/personnel')} data-id="element-1381" />
        <KPICard title="Wilayah Operasi" value={regionData.provinsi} icon={MapPin} colorVar="var(--status-info)" data-id="element-1382" />
        <KPICard title="Status Kesiapan" value="Tinggi" icon={ShieldCheck} colorVar="var(--status-active)" data-id="element-1383" />
        <KPICard title="Laporan Masuk" value={laporanMasuk.length} icon={Activity} colorVar="var(--status-standby)" onClick={() => navigate('/reports')} data-id="element-1384" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1385">
        {/* Laporan Masuk */}
        <Card className="lg:col-span-2 glass-panel border-border/50 flex flex-col overflow-hidden" data-id="element-1386">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50 flex flex-row items-center justify-between shrink-0" data-id="element-1387">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-1388">
              <Activity className="w-4 h-4 text-[var(--status-standby)]" data-id="element-1389" />{' '}
              Laporan Masuk
              {laporanMasuk.length > 0 && <span className="bg-[var(--status-standby)] text-black text-[10px] px-2 py-0.5 rounded-full font-bold" data-id="element-1390">
                  {laporanMasuk.length} BARU
                </span>}
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-[10px] font-mono" onClick={() => navigate('/reports')} data-id="element-1391">
              Lihat Semua
            </Button>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-auto" data-id="element-1392">
            <Table data-id="element-1393">
              <TableHeader className="bg-black/20" data-id="element-1394">
                <TableRow className="border-border/30 hover:bg-transparent" data-id="element-1395">
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1396">
                    ID
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1397">
                    Judul
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1398">
                    Pengirim
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1399">
                    Waktu
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-1400">
                {laporanMasuk.map(r => <TableRow key={r.id} className="border-border/20 hover:bg-white/5 transition-colors cursor-pointer" onClick={() => navigate(`/reports/${r.id}`)} data-id="element-1401">
                    <TableCell className="font-mono text-xs text-primary" data-id="element-1402">
                      {r.id}
                    </TableCell>
                    <TableCell className="font-medium text-sm" data-id="element-1403">
                      {r.title}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground" data-id="element-1404">
                      {r.sender}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground" data-id="element-1405">
                      {r.time.split(' ')[1]}
                    </TableCell>
                  </TableRow>)}
                {laporanMasuk.length === 0 && <TableRow data-id="element-1406">
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground font-mono text-xs" data-id="element-1407">
                      Tidak ada laporan masuk.
                    </TableCell>
                  </TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Personnel Status */}
        <Card className="lg:col-span-1 glass-panel border-border/50 flex flex-col overflow-hidden" data-id="element-1408">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50 flex flex-row items-center justify-between shrink-0" data-id="element-1409">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-1410">
              <Users className="w-4 h-4 text-primary" data-id="element-1411" /> Status Personel
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-[10px] font-mono" onClick={() => navigate('/personnel')} data-id="element-1412">
              Kelola
            </Button>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-auto" data-id="element-1413">
            <Table data-id="element-1414">
              <TableHeader className="bg-black/20" data-id="element-1415">
                <TableRow className="border-border/30 hover:bg-transparent" data-id="element-1416">
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1417">
                    ID
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1418">
                    Nama
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1419">
                    Status
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-1420">
                {regionalPersonnel.map(p => <TableRow key={p.id} className="border-border/20 hover:bg-white/5 transition-colors cursor-pointer" onClick={() => navigate(`/personnel/${p.id}`)} data-id="element-1421">
                    <TableCell className="font-mono text-xs text-primary" data-id="element-1422">
                      {p.id}
                    </TableCell>
                    <TableCell className="font-medium text-sm" data-id="element-1423">
                      {p.name}
                    </TableCell>
                    <TableCell data-id="element-1424">
                      <StatusBadge status={p.status === 'Aktif' ? 'Normal' : 'Offline'} data-id="element-1425" />
                    </TableCell>
                  </TableRow>)}
                {regionalPersonnel.length === 0 && <TableRow data-id="element-1426">
                    <TableCell colSpan={3} className="text-center py-8 text-muted-foreground font-mono text-xs" data-id="element-1427">
                      Tidak ada data personel.
                    </TableCell>
                  </TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Active Incidents */}
        <Card className="glass-panel border-border/50 flex flex-col overflow-hidden" data-id="element-1428">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50 flex flex-row items-center justify-between shrink-0" data-id="element-1429">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-1430">
              <AlertTriangle className="w-4 h-4 text-[var(--status-standby)]" data-id="element-1431" />{' '}
              Insiden Aktif
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-[10px] font-mono" onClick={() => navigate('/alerts')} data-id="element-1432">
              Lihat Semua
            </Button>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-auto" data-id="element-1433">
            <div className="divide-y divide-border/50" data-id="element-1434">
              {regionalAlerts.map(alert => <div key={alert.id} className="p-4 hover:bg-secondary/30 transition-colors cursor-pointer" onClick={() => navigate(`/alerts/${alert.id}`)} data-id="element-1435">
                  <div className="flex items-center gap-2 mb-1" data-id="element-1436">
                    <AlertTriangle className="w-3 h-3 text-[var(--status-standby)]" data-id="element-1437" />
                    <span className="font-mono text-xs font-bold" data-id="element-1438">
                      {alert.type}
                    </span>
                  </div>
                  <p className="text-[10px] font-mono text-muted-foreground mb-1" data-id="element-1439">
                    {alert.desc}
                  </p>
                  <span className="text-[9px] font-mono text-muted-foreground" data-id="element-1440">
                    {alert.time}
                  </span>
                </div>)}
              {regionalAlerts.length === 0 && <div className="p-8 text-center text-muted-foreground font-mono text-xs" data-id="element-1441">
                  Tidak ada insiden aktif.
                </div>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}